import React, { useMemo, useRef } from "react";
import { Accordion, useAccordionButton, OverlayTrigger, Tooltip } from "react-bootstrap";
import { ChevronDown, HelpCircle, AlertTriangle } from "react-feather";

/**
 * Module-grouped checkbox list against /api/v1/admin/permissions.
 * Shared by Roles, Designations, and the User permission-override screen -
 * the one non-trivial UI pattern that repeats across all three (handoff §10.2).
 *
 * Redesigned 2026-08-24 per direct feedback that the old flat per-module
 * grid "feels confusing... don't find it easy to remember each permission":
 * each module is now a collapsible panel with one master checkbox that
 * selects/clears everything inside it at a glance; plain Create/Edit/
 * Delete/View toggles are boxed together since they're self-explanatory,
 * while every other ("special") permission gets its own row with a "?"
 * (plain-language explanation, from the API's own `description` field) and,
 * for anything that grants elevated/irreversible capability, a "!" too.
 */
const BASIC_CRUD_ACTIONS = new Set(["Create", "Edit", "Delete", "View"]);
const SENSITIVE_ACTION_PATTERN = /Manage|Approve|Reject|Cancel|SelfApproval|Override|All$/i;

const actionOf = (permissionCode) => permissionCode.split(".").slice(1).join(".") || permissionCode;

// A "ViewAll" permission only *widens scope* on top of the base "View"
// (own-only -> everyone's) - it was never meant to be grantable on its
// own, but nothing stopped an admin from ticking ViewAll without View,
// producing a role that gets a clean 403 on every List/Detail call
// despite "having ViewAll". Hit for real twice (Collections' testeradmin,
// PettyCash's FINANCE_MANAGER) before this got fixed here. Bundling the
// two checkboxes removes the invalid combination at the source instead of
// relying on admins remembering the pairing.
const pairedToggles = (group, perm, checked) => {
  const extra = [];
  if (checked && perm.permissionCode.endsWith(".ViewAll")) {
    const baseCode = perm.permissionCode.replace(/ViewAll$/, "View");
    const viewPerm = group.items.find((p) => p.permissionCode === baseCode);
    if (viewPerm) extra.push(viewPerm);
  }
  if (!checked && perm.permissionCode.endsWith(".View")) {
    const viewAllCode = perm.permissionCode.replace(/View$/, "ViewAll");
    const viewAllPerm = group.items.find((p) => p.permissionCode === viewAllCode);
    if (viewAllPerm) extra.push(viewAllPerm);
  }
  return extra;
};

// Custom accordion toggle (react-bootstrap's "Custom Toggles" pattern) -
// needed so the module name/chevron area expands the panel while the
// master checkbox and count badge next to it stay independently clickable,
// not nested inside the header's own toggle <button>.
const ModuleToggle = ({ eventKey, children }) => {
  const onClick = useAccordionButton(eventKey);
  return (
    <button
      type="button"
      className="btn btn-link p-0 text-decoration-none d-flex align-items-center gap-2 flex-grow-1 text-start"
      onClick={onClick}
      style={{ color: "inherit" }}
    >
      {children}
    </button>
  );
};

const InfoIcon = ({ text }) =>
  text ? (
    <OverlayTrigger placement="top" overlay={<Tooltip>{text}</Tooltip>}>
      <HelpCircle size={13} className="text-muted ms-1" style={{ cursor: "help", verticalAlign: -2 }} />
    </OverlayTrigger>
  ) : null;

const WarningIcon = () => (
  <OverlayTrigger placement="top" overlay={<Tooltip>Grants elevated or hard-to-undo access - assign carefully.</Tooltip>}>
    <AlertTriangle size={13} className="text-warning ms-1" style={{ cursor: "help", verticalAlign: -2 }} />
  </OverlayTrigger>
);

// Tri-state header checkbox (checked / unchecked / indeterminate) - plain
// HTML checkboxes only support "indeterminate" via the DOM property, not a
// prop, so it's set imperatively through a ref.
const ModuleMasterCheckbox = ({ allSelected, someSelected, disabled, onChange }) => {
  const ref = useRef(null);
  return (
    <input
      ref={(el) => {
        ref.current = el;
        if (el) el.indeterminate = !allSelected && someSelected;
      }}
      type="checkbox"
      className="form-check-input mt-0"
      checked={allSelected}
      disabled={disabled}
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => {
        e.stopPropagation();
        onChange(e.target.checked);
      }}
      title={allSelected ? "Clear all permissions in this module" : "Grant all permissions in this module"}
    />
  );
};

const PermissionChecklist = ({
  permissions = [],
  selectedIds = [],
  onToggle,
  disabled = false,
}) => {
  const selected = useMemo(() => new Set(selectedIds), [selectedIds]);

  const grouped = useMemo(() => {
    const map = new Map();
    permissions.forEach((perm) => {
      if (!map.has(perm.moduleCode)) {
        map.set(perm.moduleCode, { moduleCode: perm.moduleCode, moduleName: perm.moduleName, items: [] });
      }
      map.get(perm.moduleCode).items.push(perm);
    });
    return Array.from(map.values()).sort((a, b) => a.moduleName.localeCompare(b.moduleName));
  }, [permissions]);

  // Modules that already have at least one permission granted start expanded
  // - an admin opening this modal almost always wants to see "what does this
  // role already have", not hunt through every panel to find out.
  const defaultActiveKeys = useMemo(
    () => grouped.filter((g) => g.items.some((p) => selected.has(p.permissionId))).map((g) => g.moduleCode),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  if (grouped.length === 0) {
    return <p className="text-muted mb-0">No permissions available.</p>;
  }

  const handleToggle = (group, perm, checked) => {
    onToggle(perm.permissionId, checked);
    pairedToggles(group, perm, checked).forEach((p) => {
      if (selected.has(p.permissionId) !== checked) onToggle(p.permissionId, checked);
    });
  };

  const handleToggleModule = (group, checked) => {
    group.items.forEach((perm) => {
      if (selected.has(perm.permissionId) !== checked) handleToggle(group, perm, checked);
    });
  };

  return (
    <Accordion alwaysOpen defaultActiveKey={defaultActiveKeys} className="permission-checklist">
      {grouped.map((group) => {
        const basicItems = group.items.filter((p) => BASIC_CRUD_ACTIONS.has(actionOf(p.permissionCode)));
        const specialItems = group.items.filter((p) => !BASIC_CRUD_ACTIONS.has(actionOf(p.permissionCode)));
        const selectedCount = group.items.filter((p) => selected.has(p.permissionId)).length;
        const allSelected = selectedCount === group.items.length;
        const someSelected = selectedCount > 0;

        return (
          <Accordion.Item eventKey={group.moduleCode} key={group.moduleCode} className="mb-2 border rounded overflow-hidden">
            <div
              className="d-flex align-items-center gap-2 px-3 py-2"
              style={{ background: "#6E1423", color: "#fff" }}
            >
              <ModuleMasterCheckbox
                allSelected={allSelected}
                someSelected={someSelected}
                disabled={disabled}
                onChange={(checked) => handleToggleModule(group, checked)}
              />
              <ModuleToggle eventKey={group.moduleCode}>
                <ChevronDown size={15} className="accordion-chevron" style={{ color: "#fff" }} />
                <span className="fw-semibold" style={{ color: "#fff" }}>{group.moduleName}</span>
              </ModuleToggle>
              <span
                className="badge"
                style={{
                  background: someSelected ? "#fff" : "rgba(255,255,255,0.25)",
                  color: someSelected ? "#6E1423" : "#fff",
                }}
              >
                {selectedCount} / {group.items.length}
              </span>
            </div>
            <Accordion.Body className="pt-3">
              {basicItems.length > 0 && (
                <div className="d-flex flex-wrap gap-3 border rounded p-2 mb-3 bg-white">
                  {basicItems.map((perm) => (
                    <div className="form-check mb-0" key={perm.permissionId}>
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id={`perm-${perm.permissionId}`}
                        checked={selected.has(perm.permissionId)}
                        disabled={disabled}
                        onChange={(e) => handleToggle(group, perm, e.target.checked)}
                      />
                      <label className="form-check-label" htmlFor={`perm-${perm.permissionId}`}>
                        {actionOf(perm.permissionCode)}
                      </label>
                    </div>
                  ))}
                </div>
              )}

              {specialItems.length > 0 && (
                <div className="row">
                  {specialItems.map((perm) => (
                    <div className="col-md-6 mb-2" key={perm.permissionId}>
                      <div className="form-check mb-0">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id={`perm-${perm.permissionId}`}
                          checked={selected.has(perm.permissionId)}
                          disabled={disabled}
                          onChange={(e) => handleToggle(group, perm, e.target.checked)}
                        />
                        <label className="form-check-label" htmlFor={`perm-${perm.permissionId}`}>
                          {actionOf(perm.permissionCode)}
                        </label>
                        <InfoIcon text={perm.description} />
                        {SENSITIVE_ACTION_PATTERN.test(actionOf(perm.permissionCode)) && <WarningIcon />}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Accordion.Body>
          </Accordion.Item>
        );
      })}
    </Accordion>
  );
};

export default PermissionChecklist;
